import { Component, OnInit, Input, ViewChild, AfterViewInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ReportAspectTextViewModel, ReportAspectTextInfoViewModel } from '../../index';
import { ReportService } from '../../api/report.service';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { ReportHelperService } from '../../../services/report-helper.service';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-report-aspect-text-list',
    templateUrl: './report-aspect-text-list.component.html',
    styleUrls: ['./report-aspect-text-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportAspectTextListComponent implements OnInit {
    reportAspectGroupId: number;
    reportAspectGroupName: string;
    hasPermissionToChange$: Observable<boolean>;

    aspectTextForm: UntypedFormGroup;

    loadingIndicator: boolean;
    isSaving: boolean = false;
    anySaveOperationsPerformed: boolean = false;

    infoReportAspectTextId: number = null;
    info: ReportAspectTextInfoViewModel;

    scoreLevels: Array<[Number, string]>;

    public textCount: number = 0;
    public finalizedTextCount: number = 0;

    constructor(
        public dialogRef: MatDialogRef<ReportAspectTextListComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private reportService: ReportService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        private reportHelperService: ReportHelperService) {

        this.reportAspectGroupId = data.reportAspectGroupId;
        this.reportAspectGroupName = data.reportAspectGroupName;
        this.hasPermissionToChange$ = data.hasPermissionToChange$;
    }

    ngOnInit() {
        this.buildForm();
        this.scoreLevels = this.reportHelperService.getScoreLevels();
        this.loadData();

        // Use this construct to handle the escape key for cancelling
        this.dialogRef.keydownEvents().pipe(map(r => new test(r, this))).subscribe((e) => {
                if (e.event.key === "Escape") {
                    //e.event.stopPropagation();
                    e.component.cancel();
                }
            }
        );
    }


    buildForm() {
        this.aspectTextForm = this.formBuilder.group({
            aspectTexts: this.formBuilder.array([])
        });
    }


    buildChildArray() : UntypedFormGroup {
        return this.formBuilder.group({
            reportAspectTextId: '',
            name: '',
            scoreLevel: '',
            previousScoreLevel: ''
        });
    }

    resetForm(aspectTexts: ReportAspectTextViewModel[]) {
        this.aspectTextForm.reset();
        this.textCount = aspectTexts.length;
        this.finalizedTextCount = aspectTexts.filter(t => this.isAssigned(t)).length;

        let aspectTextFormGroups = aspectTexts.map(txt => this.formBuilder.group(txt));
        let aspectTextFormArray = this.formBuilder.array(aspectTextFormGroups);
        this.aspectTextForm.setControl('aspectTexts', aspectTextFormArray);
    }

    get aspectTextsArray(): UntypedFormArray {
        return this.aspectTextForm.get('aspectTexts') as UntypedFormArray;
    }


    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.reportService.getAspectTextsByReportAspectGroupId(this.reportAspectGroupId).subscribe(
            reqs => this.onDataLoadSuccessful(reqs),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(aspectTexts: ReportAspectTextViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.resetForm(aspectTexts);
    }

    private isAssigned(aspectText: ReportAspectTextViewModel): boolean {
        return aspectText.scoreLevel !== null;
    }

    selectionChange(event: MatSelectChange): void {
        var arr = this.aspectTextsArray;
        var counter = 0;
        for (var i = 0; i < arr.length; i++) {
            if (arr.at(i).get("scoreLevel").value) {
                counter++;
            }
        }

        this.finalizedTextCount = counter;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }

    public toggleInfo(id: any): void {
        if (this.infoReportAspectTextId == null) {
            this.infoReportAspectTextId = id;

            this.reportService.getAspectTextInfoByReportAspectTextId(id).subscribe(
                reqs => this.onDataInfoLoadSuccessful(reqs),
                error => this.onDataLoadFailed(error));
        } else {
            this.infoReportAspectTextId = null;
        }
    }

    private onDataInfoLoadSuccessful(info: ReportAspectTextInfoViewModel) {
        this.info = info;
    }

    public hideInfo(): void {
        this.infoReportAspectTextId = null;
    }

    public getListRowStyle(reportAspectTextId: number): any {
        if (this.infoReportAspectTextId === null || this.infoReportAspectTextId === reportAspectTextId) {
            return "";
        } else {
            return { 'display' : 'none' };
        }
    }

    public  getScoreLevelName(scoreLevel: number): string {
        var foundLevel = this.scoreLevels.find(t => t[0] === scoreLevel);
        if (foundLevel) {
            if (scoreLevel >= 0) {
                return scoreLevel + " - " + foundLevel[1];
            } else {
                return foundLevel[1];
            }
        }

        return "";
    }

    public getScoreLevelText(scoreLevel: [ number, string ]): string {
        if (scoreLevel[0] >= 0) {
            return scoreLevel[0] + " - " + scoreLevel[1];
        } else {
            return scoreLevel[1];
        }
    }

    public save(close: boolean) {
        if (!this.aspectTextForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        let container = this.aspectTextForm.value;
        let aspectTexts: ReportAspectTextViewModel[] = container.aspectTexts;

        this.reportService.updateReportAspectTexts(aspectTexts).subscribe(
            t => this.saveCompleted(close, t),
            error => this.saveFailed(error));

    }

    private saveCompleted(close: boolean, aspectTexts: ReportAspectTextViewModel[]) {
        this.isSaving = false;
        this.anySaveOperationsPerformed = true;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"),
            "Aspekter " + this.translationService.getTranslation("globalTexts.saved"),
            MessageSeverity.success);

        if (close) {
            this.dialogRef.close(true);
        } else {
            this.resetForm(aspectTexts);
        }
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"),
            this.translationService.getTranslation("globalTexts.SaveErrorMessage"),
            MessageSeverity.error,
            error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }


    public cancel() {
        this.alertService.resetStickyMessage();

        if (this.aspectTextForm.dirty) {
            this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                    this.translationService.getTranslation("commands.OK"),
                    { duration: 5000 })
                .onAction().subscribe(() => {
                    this.dialogRef.close(this.anySaveOperationsPerformed);
                    }
                );
        } else {
            this.dialogRef.close(this.anySaveOperationsPerformed);
        }
    }

}

export class test {
    component: ReportAspectTextListComponent;
    event: KeyboardEvent;

    constructor(event: KeyboardEvent, component: ReportAspectTextListComponent) {
        this.component = component;
        this.event = event;
    }
}
